import React, { useState } from 'react';
import { Menu } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_SIDEBAR } from '../../constants';
import { useAuth } from '../../context/AuthContext';
import Avatar from '../Avatar';
import SidebarIcon from '../SidebarIcon';

const AdminLayout = ({ title, children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#F5F8F6] font-dm text-giggs-ink">
      <title>GIGGS — {title}</title>
      <link rel="icon" href="/images/favicon.png" type="image/png" />

      <aside
        className={`fixed inset-y-0 left-0 z-[100] flex w-[260px] flex-col bg-giggs-dark transition-transform duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-white/[0.06] px-6 pb-5 pt-6">
          <img src="/images/logo-white.png" alt="GIGGS" className="h-10 w-auto" />
          <button
            type="button"
            className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-white/50 transition-colors hover:text-white lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
          {ADMIN_SIDEBAR.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.label}
                to={item.path}
                className={`flex items-center gap-3.5 rounded-xl px-4 py-3 text-sm font-medium no-underline transition-all duration-200 ${
                  active
                    ? 'bg-[rgba(0,166,81,0.15)] text-white [&_svg]:text-giggs-mint'
                    : 'text-white/50 hover:bg-white/[0.05] hover:text-white/85'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <SidebarIcon type={item.icon} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-white/[0.06] px-3 pb-6 pt-4">
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-3.5 rounded-xl border-0 bg-transparent px-4 py-3 text-left text-sm font-medium text-white/45 transition-all duration-200 hover:bg-red-500/[0.08] hover:text-red-500"
            onClick={handleLogout}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" /></svg>
            <span>Log Out</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-[99] cursor-default border-0 bg-black/40 p-0 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close navigation overlay"
        />
      )}

      <div className="flex min-h-screen min-w-0 flex-col lg:ml-[260px]">
        <header className="sticky top-0 z-50 flex min-h-[76px] items-center gap-4 bg-white/90 px-4 py-3 shadow-[0_1px_0_rgba(15,23,42,0.04),0_8px_28px_rgba(15,23,42,0.035)] backdrop-blur-xl sm:px-5 lg:px-8">
          <button
            type="button"
            className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl border-0 bg-slate-100 p-0 text-giggs-ink transition-all duration-200 hover:bg-giggs-green/10 hover:text-giggs-green lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={21} />
          </button>

          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-giggs-muted">Administration</div>
            <h1 className="m-0 mt-0.5 truncate font-nunito text-[clamp(1.15rem,2vw,1.45rem)] font-black tracking-[-0.02em] text-giggs-ink">{title}</h1>
          </div>

          <div className="flex shrink-0 items-center gap-3 rounded-2xl bg-[#F7F9F8] px-2.5 py-2 sm:px-3">
            {user && (
              <div className="hidden min-w-0 text-right sm:block">
                <div className="max-w-[180px] truncate text-xs font-bold text-giggs-ink">{user.name || 'Administrator'}</div>
                <div className="text-[10px] font-medium text-giggs-muted">Admin</div>
              </div>
            )}
            <Avatar user={user} size={38} />
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 sm:px-5 sm:py-6 lg:px-8 lg:py-8 2xl:px-10">
          <div className="mx-auto w-full max-w-[1900px]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
